import { html } from './html.js';
import type { VNode } from './html.js';
import type { Actions } from './actions.js';
import type { ConsoleState } from './store.js';
import { LandingScreen } from './screens/landing.js';
import { ProgressScreen } from './screens/progress.js';
import { SignInScreen } from './screens/signin.js';
import { ResultScreen } from './screens/result.js';

/**
 * The frame: a rail, one screen, a foot.
 *
 * There is no router. The console has exactly one job with exactly one path
 * through it, and inventing URLs for the middle of a streaming crawl would
 * promise a resumability the job store does not have.
 *
 * The rail carries the one link that leaves: to the dashboard, on this same
 * origin. Until this existed, a person who had already published had no way in
 * from either the website or here — the only sign-in form in the product was
 * behind a screen you reached by finishing a crawl. The label follows the
 * server's answer to `GET /api/auth/me`: "Sign in" for a stranger, "Dashboard"
 * once the cookie is known, and nothing at all in demo mode, where there is no
 * account service to sign in to.
 */
export function ConsoleView(props: {
  state: ConsoleState;
  actions: Actions;
  now: number;
}): VNode {
  const { state, actions, now } = props;

  return html`
    <div>
      <div class="rail">
        <div class="wrap rail__in">
          <button
            type="button"
            class="mark"
            onClick=${() => actions.restart()}
            disabled=${state.screen === 'landing'}
            aria-label="ClawHarbor — start over"
          >
            <span class="mark__flag" aria-hidden="true"></span>ClawHarbor
          </button>
          <span class="rail__spacer"></span>
          ${state.mode === 'demo'
            ? html`<span class="chip chip--demo" title="A recorded crawl, replayed from disk. No site is being fetched.">
                <i class="chip__dot" aria-hidden="true"></i>Demo data
              </span>`
            : html`<span class="chip chip--live">
                <i class="chip__dot" aria-hidden="true"></i>Live
              </span>`}
          ${state.mode === 'demo'
            ? null
            : state.principal
              ? html`<a
                  class="linkish rail__dash"
                  href="/dashboard/"
                  title="Your tenants, your crawls, and who else can operate them"
                  >Dashboard</a
                >`
              : html`<a
                  class="linkish rail__dash"
                  href="/dashboard/"
                  title="Sign in with a link sent to your email. There is no password."
                  >Sign in</a
                >`}
          ${state.screen !== 'landing'
            ? html`<button type="button" class="linkish" onClick=${() => actions.restart()}>
                New crawl
              </button>`
            : null}
        </div>
      </div>

      <main>
        ${state.screen === 'landing'
          ? LandingScreen({ state, actions })
          : state.screen === 'signin'
            ? SignInScreen({ state, actions })
            : state.screen === 'progress'
              ? ProgressScreen({ state, actions, now })
              : ResultScreen({ state, actions })}
      </main>

      <footer class="wrap foot">
        Third-party sites are crawled read-only as ClawHarborBot/1.0 at one request per second,
        honouring robots.txt, never behind a login. No generated tool can write, book, pay, or
        authenticate — the strongest kind is <code>stage</code>, which returns a deep link a person
        has to confirm. Takedown: clawharbor.io/takedown
      </footer>
    </div>
  `;
}
